/**
 * Implements MyPositions based on covalent api
 */
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import LimitOrdersTable from './Table';
import useCollectTickers from '../useCollectTickers';
import { IStore } from '../../../reducers';
import { KattanaAPI } from '../../../store/createStore';


/**
 * Parent component used to load tickers before renderings
 * @param props
 */
const LimitOrdersDataLayer = ({ width }: { width: number }) => {
  const { loading, orders } = useSelector((state: IStore) => state.limitOrders);
  const [filter, setFilter] = useState('');
  const [names, setNames] = useState<Record<string, string>>({});

  const data = React.useMemo(
    () =>
      orders.filter((order) =>
        filter ? filter === `${order.chain}/${order.pair}` : true
      ),
    [orders, filter]
  );

  useEffect(() => {
    const controller = new AbortController();

    const uniqueData = orders.reduce(
      (acc: Record<string, { chain: string; address: string }>, order) => {
        const srcKey = `${order.chain}-${order.src_address}`;
        const destKey = `${order.chain}-${order.dest_address}`;
        if (!acc[srcKey]) {
          acc[srcKey] = { chain: order.chain, address: order.src_address };
        }
        if (!acc[destKey]) {
          acc[destKey] = { chain: order.chain, address: order.dest_address };
        }
        return acc;
      },
      {}
    );

    Promise.all(
      Object.values(uniqueData).map(async (data) => {
        const srcToken = await KattanaAPI.tokenInfo(
          data.chain,
          data.address,
          {
            signal: controller.signal,
          }
        );
        return {
          name: srcToken?.slug || '',
          address: data.address,
        };
      })
    ).then((tokens) =>
      setNames(
        tokens.reduce((acc: Record<string, string>, data) => {
          const { name, address } = data;
          acc[address] = name;
          return acc;
        }, {})
      )
    );

    return () => {
      controller.abort();
    };
  }, [orders]);

  const { tickers, loadingTickers } = useCollectTickers(data);

  return (
    <LimitOrdersTable
      tickers={tickers}
      orders={data}
      names={names}
      filter={filter}
      onFilter={setFilter}
      loading={loading || loadingTickers}
      width={width}
    />
  );
};

export default LimitOrdersDataLayer;
